// Import and re-export shared parameter types
import type { Parameter, ParameterTypeOption } from "../../utils/parameterSchema";
export type { Parameter, ParameterTypeOption };

export type WizardStep = 1 | 2 | 3 | 4;

export type CapabilityTypeOption = "GITHUB_ACTIONS_WORKFLOW" | "BUILDKITE_PIPELINE";
export type ApprovalTypeOption = "AUTOMATIC" | "MANUAL";

export type GithubRepo = {
  id: number;
  full_name: string;
  name: string;
  default_branch: string;
};

export type GithubWorkflow = {
  id: number;
  name: string;
  is_usable?: boolean;
  error_message?: string;
};

export type BuildkitePipeline = {
  slug: string;
  name: string;
};

export interface WizardState {
  currentStep: WizardStep;
  // Step 1: Workflow Selection
  capabilityType: CapabilityTypeOption;
  selectedIntegrationId: string;
  selectedGitHubOrgId: string;
  selectedGitHubRepo: string;
  selectedGitHubWorkflow: string;
  selectedBuildkiteOrgId: string;
  selectedBuildkitePipeline: string;
  // Loaded data from Step 1 (needed for submission)
  githubRepos: GithubRepo[];
  githubWorkflows: GithubWorkflow[];
  buildkitePipelines: BuildkitePipeline[];
  // Step 2: Configuration
  description: string;
  approvalType: ApprovalTypeOption;
  // Step 3: Review
  name: string;
  parameters: Parameter[];
  showAdvanced: boolean;
  jsonSchema: string;
  expandedParams: Set<string>;
  // Default branch values
  githubWorkflowDefaultBranch: string;
  buildkitePipelineDefaultBranch: string;
  // Flag to track if we've auto-set the name
  hasSetDefaultName: boolean;
}

export const WIZARD_STEP_TITLES: Record<WizardStep, string> = {
  1: "Select Pipeline",
  2: "Parameters",
  3: "Configure",
  4: "Review",
};

export const getWizardStepDescriptions = (
  productName: string
): Record<WizardStep, string> => ({
  1: `Give ${productName} access to call a workflow or pipeline`,
  2: "Define parameters to customize behavior of the pipelines",
  3: `Describe when ${productName} should call this workflow or pipeline and if it should be gated by human approval`,
  4: "Review your configuration and create the capability",
});

export const CAPABILITY_TYPE_LABELS: Record<CapabilityTypeOption, string> = {
  GITHUB_ACTIONS_WORKFLOW: "GitHub Actions Workflow",
  BUILDKITE_PIPELINE: "Buildkite Pipeline",
};

export const APPROVAL_TYPE_LABELS: Record<ApprovalTypeOption, string> = {
  MANUAL: "Requires Approval",
  AUTOMATIC: "Automatic",
};

export const getDefaultCapabilityName = (
  capabilityType: CapabilityTypeOption,
  workflowName?: string,
  pipelineName?: string
): string => {
  if (capabilityType === "GITHUB_ACTIONS_WORKFLOW" && workflowName) {
    return workflowName;
  }
  if (capabilityType === "BUILDKITE_PIPELINE" && pipelineName) {
    return pipelineName;
  }
  return "";
};

export const createInitialWizardState = (): WizardState => ({
  currentStep: 1,
  capabilityType: "GITHUB_ACTIONS_WORKFLOW",
  selectedIntegrationId: "",
  selectedGitHubOrgId: "",
  selectedGitHubRepo: "",
  selectedGitHubWorkflow: "",
  selectedBuildkiteOrgId: "",
  selectedBuildkitePipeline: "",
  githubRepos: [],
  githubWorkflows: [],
  buildkitePipelines: [],
  description: "",
  approvalType: "MANUAL",
  name: "",
  parameters: [],
  showAdvanced: false,
  jsonSchema: "{}",
  expandedParams: new Set(),
  githubWorkflowDefaultBranch: "main",
  buildkitePipelineDefaultBranch: "main",
  hasSetDefaultName: false,
});
